import React from "react";

import { ApiStructuredContent } from "../../api/chats.api";
import {
  ChatMessage as ChatMessageType,
  MaiteChatMessage,
  PreDefinedConstraints,
} from "../../types";
import { AssistantMessage } from "./messages/AssistantMessage";
import { ErrorMessage } from "./messages/ErrorMessage";
import { UserMessage } from "./messages/UserMessage";
import { ScenarioBuilderDisclaimer } from "./ScenarioBuilderDisclaimer";
import { areMessagesEqual } from "./utils";

interface ChatMessageProps {
  message: ChatMessageType;
}

// Type guards
const isUserMessage = (
  message: ChatMessageType
): message is { user: "user"; content: string; preSetConstraints?: PreDefinedConstraints } => {
  return message.user === "user";
};

const isMaiteErrorMessage = (
  message: ChatMessageType
): message is Extract<MaiteChatMessage, { isError: true }> => {
  return message.user === "maite" && "isError" in message && message.isError === true;
};

const isMaiteSuccessMessage = (
  message: ChatMessageType
): message is Extract<MaiteChatMessage, { isError: false }> => {
  return message.user === "maite" && "isError" in message && message.isError === false;
};

const isMaiteAssistantMessage = (
  message: ChatMessageType
): message is Extract<
  ChatMessageType,
  { isError: false; structuredContent: ApiStructuredContent }
> => {
  return (
    message.user === "maite" &&
    "isError" in message &&
    message.isError === false &&
    "content" in message
  );
};

const ChatMessageComponent: React.FC<ChatMessageProps> = ({ message }: ChatMessageProps) => {
  if (isUserMessage(message)) {
    return <UserMessage content={message.content} preSetConstraints={message.preSetConstraints} />;
  }

  if (isMaiteErrorMessage(message)) {
    return (
      <>
        <ErrorMessage error={message.content} />
        {message.isScenariosQuery && <ScenarioBuilderDisclaimer />}
      </>
    );
  }

  if (isMaiteSuccessMessage(message)) {
    return (
      <>
        <AssistantMessage structuredContent={message.structuredContent} debug={message.debug} />
        {message.isScenariosQuery && <ScenarioBuilderDisclaimer />}
      </>
    );
  }

  if (isMaiteAssistantMessage(message)) {
    const maiteMessage = message as Extract<MaiteChatMessage, { isError: false }>;
    return (
      <>
        <AssistantMessage structuredContent={message} debug={maiteMessage.debug} />
        {maiteMessage.isScenariosQuery && <ScenarioBuilderDisclaimer />}
      </>
    );
  }

  return null;
};

export const ChatMessage = React.memo(ChatMessageComponent, (prev, next) =>
  areMessagesEqual(prev.message as ChatMessageType, next.message as ChatMessageType)
);
